import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Link } from 'react-router-dom';

interface PrivacyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PrivacyModal({ open, onOpenChange }: PrivacyModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl">개인정보 처리방침</DialogTitle>
          <DialogDescription>
            ALT Generation 서비스 개인정보 처리방침을 확인해주세요. (시행일: 2026년 4월 1일)
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto max-h-[60vh] pr-4">
          <div className="space-y-6 text-sm">

            <p className="text-gray-700 leading-relaxed">
              유니버싱정보출판(이하 "회사")은 ALT Generation 서비스와 관련하여 「개인정보 보호법」 및 관계 법령에 따라 이용자의 개인정보를 보호하고 이와 관련한 고충을 신속원활하게 처리하기 위하여 다음과 같이 개인정보 처리방침을 수립공개합니다.
            </p>

            <section>
              <h3 className="font-bold text-base mb-2">제1조 (개인정보의 처리 목적)</h3>
              <p className="text-gray-700 leading-relaxed mb-2">회사는 다음의 목적을 위해 개인정보를 처리합니다. 처리된 개인정보는 아래 목적 이외의 용도로 이용되지 않으며, 목적이 변경되는 경우 별도 동의를 받습니다.</p>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li><strong>회원 가입 및 관리:</strong> 본인 식별인증, 회원자격 유지관리, 부정이용 방지, 고지통지, 고충 처리</li>
                <li><strong>서비스 제공:</strong> ALT 텍스트 생성 서비스(웹 크롤링, URL 분석, 이미지 업로드), API 서비스 제공, 요금 결제 및 정산</li>
                <li><strong>서비스 개선:</strong> 이용 통계 분석, 신규 서비스 개발</li>
                <li><strong>마케팅 및 광고 (선택 동의 시):</strong> 이벤트프로모션 안내, 신규 기능 소식 전달</li>
                <li><strong>법적 의무 이행:</strong> 전자상거래법 등 관계 법령에 따른 기록 보관</li>
              </ol>
            </section>

            <section>
              <h3 className="font-bold text-base mb-2">제2조 (처리하는 개인정보의 항목)</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li><strong>회원가입 시 필수항목:</strong> 로그인 아이디, 비밀번호(BCrypt 암호화 저장), 이름, 이메일 주소</li>
                <li><strong>회원가입 시 선택항목:</strong> 휴대전화번호, 회사명, 사업자번호</li>
                <li><strong>소셜 로그인 시 수집 항목:</strong>
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>Google 로그인: 이메일, 이름 (Google 제공 정보에 한함)</li>
                    <li>네이버 로그인: 이메일, 이름 (네이버 제공 정보에 한함)</li>
                    <li>카카오 로그인: 이메일, 닉네임 (카카오 제공 정보에 한함)</li>
                  </ul>
                </li>
                <li><strong>서비스 이용 시 자동 수집 항목:</strong>
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>접속 IP 주소, 브라우저 정보(User-Agent), OS 정보, 방문 일시</li>
                    <li>로그인 세션 정보: 디바이스 정보(OS·브라우저), 로그인 시각, 마지막 활동 시각 (중복 로그인 방지 및 보안 목적)</li>
                    <li>서비스 이용 기록: ALT 텍스트 생성 이력, API 호출 기록, 주요 액션 감사 로그(보안 사고 추적 및 부정이용 방지 목적)</li>
                    <li>크롤링 대상 URL, 이미지 URL 및 메타데이터</li>
                    <li>웹사이트 콘텐츠: 텍스트, 이미지, 하이퍼링크 (ALT 텍스트 생성 및 접근성 분석 목적)</li>
                    <li>컨텍스트 프로필 설정 정보: 업종 유형, 서비스 대상, 출력 톤, 필수 포함어, 금지어, 커스텀 지시사항 (맞춤형 ALT 텍스트 생성 목적)</li>
                    <li>API 인증 정보: API Key (서비스 이용 인증 목적)</li>
                    <li>Umami(쿠키를 사용하지 않는 웹사이트 통계 도구) 이용 통계 정보: 방문 페이지, 클릭 이벤트, 유입 경로, 브라우저·운영체제·기기 유형, 접속 국가 (쿠키나 개별 방문자 식별자 없이 익명화된 형태로 수집, 웹사이트 이용 통계 분석 목적)</li>
                  </ul>
                </li>
                <li><strong>이미지 업로드 시:</strong> 이용자가 업로드한 이미지 파일은 이용자가 직접 삭제하거나 회원 탈퇴 시 삭제됩니다. 생성된 ALT 텍스트 결과물은 이용자 삭제 요청 시 또는 탈퇴 후 1년 보존 기간 경과 후 삭제됩니다.</li>
              </ol>
            </section>

            <section>
              <h3 className="font-bold text-base mb-2">제3조 (개인정보의 처리 및 보유기간)</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>회사는 법령에 따른 보유기간 또는 이용자 동의 기간 내에서만 개인정보를 보유처리합니다.</li>
                <li>각 항목별 보유기간은 다음과 같습니다.
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>회원정보: 탈퇴 후 1년간 보존 후 자동 삭제 (탈퇴 취소·복구 목적, 1년 경과 후 완전 삭제)</li>
                    <li>ALT 텍스트 생성 이력: 이용자 삭제 요청 시 즉시 삭제, 회원 탈퇴 시 탈퇴 후 1년간 보존 후 자동 삭제</li>
                    <li>업로드 이미지 파일: 이용자 직접 삭제 또는 탈퇴 후 1년 보존 기간 경과 후 삭제</li>
                    <li>로그인 세션 정보: 세션 만료 후 자동 삭제 (기본 30분 비활성 시 만료, 만료 후 최대 7일 이내 완전 삭제)</li>
                    <li>관리자 접속기록(감사 로그): 「개인정보의 안전성 확보조치 기준」 제8조에 따라 2년간 보관 후 자동 삭제</li>
                    <li>결제거래 기록: 전자상거래법에 따라 5년</li>
                    <li>소비자 불만분쟁 기록: 전자상거래법에 따라 3년</li>
                  </ul>
                </li>
              </ol>
            </section>

            <section>
              <h3 className="font-bold text-base mb-2">제4조 (개인정보의 제3자 제공)</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                회사는 이용자의 개인정보를 원칙적으로 외부에 제공하지 않습니다. 다음의 경우에는 예외입니다.
              </p>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>이용자가 사전에 동의한 경우</li>
                <li>법령의 규정에 의하거나, 수사 목적으로 법령에 정해진 절차방법에 따라 수사기관이 요청하는 경우</li>
                <li>이용자의 생명신체재산 보호를 위해 긴급히 필요하나 동의 받기 어려운 경우</li>
              </ol>
            </section>

            <section>
              <h3 className="font-bold text-base mb-2">제5조 (개인정보 처리의 위탁)</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                회사는 서비스 제공을 위해 아래와 같이 개인정보 처리를 국내 수탁업체에 위탁하고 있으며, 위탁계약 시 개인정보가 안전하게 관리되도록 필요한 사항을 규정합니다.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200 text-xs mt-2">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-200 px-3 py-2 text-left">수탁업체</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">위탁 업무</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">보유이용기간</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">Amazon Web Services (AWS) 서울 리전</td>
                      <td className="border border-gray-200 px-3 py-2">서버 호스팅, 데이터 저장 (국내 서버 운영)</td>
                      <td className="border border-gray-200 px-3 py-2">이용계약 종료 시까지</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">토스페이먼츠(주)</td>
                      <td className="border border-gray-200 px-3 py-2">카드 결제 및 자동(정기)결제 대행, 결제 정보 처리</td>
                      <td className="border border-gray-200 px-3 py-2">회원 탈퇴 또는 위탁계약 종료 시까지 (전자상거래법에 따른 거래기록은 별도 보존기간 적용)</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">바로빌</td>
                      <td className="border border-gray-200 px-3 py-2">전자세금계산서·현금영수증 발행 대행</td>
                      <td className="border border-gray-200 px-3 py-2">관계 법령(전자상거래법 등)에 따른 보존기간까지</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">Umami Software, Inc.</td>
                      <td className="border border-gray-200 px-3 py-2">웹사이트 방문자 트래픽 및 서비스 이용 통계 분석</td>
                      <td className="border border-gray-200 px-3 py-2">무료 요금제(Hobby) 기준 최대 6개월간 보관 후 자동 삭제</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h3 className="font-bold text-base mb-2">제6조 (개인정보의 국외 이전)</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                회사는 AI 기반 ALT 텍스트 생성 기능 제공을 위해 아래와 같이 개인정보를 국외로 이전합니다. 서버 호스팅(AWS)은 국내(서울 리전)에서 이루어지므로 본 조의 국외 이전 대상이 아닙니다.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200 text-xs mt-2">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-200 px-3 py-2 text-left">이전받는 자</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">이전 항목</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">이전 국가</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">이전 시기 및 방법</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">이용목적 및 보유·이용기간</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">OpenAI, L.L.C.<br />(privacy@openai.com)</td>
                      <td className="border border-gray-200 px-3 py-2">업로드·크롤링된 이미지 및 관련 텍스트 콘텐츠</td>
                      <td className="border border-gray-200 px-3 py-2">미국</td>
                      <td className="border border-gray-200 px-3 py-2">ALT 텍스트 생성 요청 시점에 API(HTTPS 암호화 통신)로 즉시 전송</td>
                      <td className="border border-gray-200 px-3 py-2">AI 이미지 분석 및 ALT 텍스트 생성(주 엔진); OpenAI의 어뷰징 모니터링 정책에 따라 최대 30일간 보관 후 삭제(Zero Data Retention 미적용, 모델 학습에는 사용되지 않음)</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">Google LLC<br />(policies.google.com/privacy)</td>
                      <td className="border border-gray-200 px-3 py-2">업로드·크롤링된 이미지 및 관련 텍스트 콘텐츠, 캡션 번역용 텍스트</td>
                      <td className="border border-gray-200 px-3 py-2">미국</td>
                      <td className="border border-gray-200 px-3 py-2">ALT 텍스트 생성 요청 시점에 API(HTTPS 암호화 통신)로 즉시 전송</td>
                      <td className="border border-gray-200 px-3 py-2">AI 이미지 분석(보조 엔진, fallback) 및 캡션 번역(Azure Translator 장애 시 대체); Google Cloud 서비스 약관에 따라 처리되며 모델 학습에는 사용되지 않음</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">Microsoft Corporation<br />(privacy.microsoft.com)</td>
                      <td className="border border-gray-200 px-3 py-2">업로드·크롤링된 이미지 및 관련 텍스트 콘텐츠, 캡션 번역용 텍스트</td>
                      <td className="border border-gray-200 px-3 py-2">미국</td>
                      <td className="border border-gray-200 px-3 py-2">ALT 텍스트 생성 요청 시점 및 캡션 번역 시점에 API(HTTPS 암호화 통신)로 즉시 전송</td>
                      <td className="border border-gray-200 px-3 py-2">AI 이미지 분석(보조 엔진, fallback) 및 캡션 번역(Azure Translator); Microsoft의 어뷰징 모니터링 정책에 따라 최대 30일간 보관 후 삭제(Zero Data Retention 미적용, 모델 학습에는 사용되지 않음)</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">Umami Software, Inc.<br />(privacy@umami.is)</td>
                      <td className="border border-gray-200 px-3 py-2">방문 페이지, 클릭 이벤트 등 서비스 이용 통계 정보, 브라우저·운영체제·기기 유형, 접속 국가(쿠키·개별 방문자 식별자 없이 익명화된 형태)</td>
                      <td className="border border-gray-200 px-3 py-2">미국(Umami Cloud 서버는 미국·EU 리전에서 운영)</td>
                      <td className="border border-gray-200 px-3 py-2">웹사이트/서비스 이용 시 실시간 자동 수집·전송</td>
                      <td className="border border-gray-200 px-3 py-2">웹사이트 이용 통계 분석(방문자 수, 인기 페이지, 유입 경로 파악) 목적; 무료 요금제(Hobby) 기준 최대 6개월간 보관 후 자동 삭제</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-gray-700 leading-relaxed mt-2">
                이용자는 개인정보의 국외 이전을 거부할 권리가 있으며, 개인정보 보호책임자(제11조)에게 이메일로 거부 의사를 통지할 수 있습니다. 다만 위 국외 이전은 AI 기반 ALT 텍스트 자동 생성이라는 서비스 핵심 기능의 이행에 필수적인 처리이므로, 거부 시 이미지 업로드·크롤링을 통한 AI ALT 텍스트 자동 생성 기능은 이용하실 수 없습니다(계정 자체의 이용, 수동 ALT 텍스트 입력 등 다른 기능은 계속 이용 가능합니다). Umami를 통한 웹사이트 이용 통계 수집을 원하지 않는 경우, 브라우저의 광고·트래킹 차단 확장 프로그램을 사용하여 통계 수집 스크립트(cloud.umami.is) 로드 자체를 차단할 수 있으며, 이 경우에도 서비스의 다른 기능 이용에는 영향이 없습니다.
              </p>
            </section>

            <section>
              <h3 className="font-bold text-base mb-2">제7조 (이용자의 권리의무 및 행사방법)</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>이용자는 회사에 대해 언제든지 개인정보 열람, 정정, 삭제, 처리정지 요구 등의 권리를 행사할 수 있습니다.</li>
                <li>권리 행사는 이메일(alt.generation@outlook.com) 또는 1:1문의를 통해 요청할 수 있으며, 회사는 지체 없이 조치합니다.</li>
                <li>만 14세 미만 아동의 개인정보는 법정대리인의 동의 없이 처리하지 않습니다.</li>
                <li>이용자는 개인정보 보호법 시행령 제41조에 따른 서면, 이메일 등의 방법으로 권리 행사를 할 수 있습니다. 열람 요구는 받은 날부터 10일 이내에 열람할 수 있도록 조치하며, 정정·삭제 요구도 10일 이내에 조치 결과를 통지합니다. 처리정지 요구는 법령에 따라 지체 없이 조치합니다.</li>
              </ol>
            </section>

            <section>
              <h3 className="font-bold text-base mb-2">제8조 (개인정보의 파기)</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>회사는 보유기간이 경과하거나 처리 목적이 달성된 개인정보를 지체 없이 파기합니다.</li>
                <li>다른 법령에 따라 보존이 필요한 경우 별도 DB 또는 보관장소로 분리하여 저장합니다.</li>
                <li>파기 방법:
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>전자적 파일: 복구 불가능한 방법으로 영구 삭제</li>
                    <li>인쇄물서면: 분쇄 또는 소각</li>
                  </ul>
                </li>
              </ol>
            </section>

            <section>
              <h3 className="font-bold text-base mb-2">제9조 (개인정보의 안전성 확보조치)</h3>
              <p className="text-gray-700 leading-relaxed mb-2">회사는 개인정보의 안전성 확보를 위해 다음 조치를 취합니다.</p>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li><strong>관리적 조치:</strong> 내부관리계획 수립시행, 접근 권한 최소화, 직원 보안 교육</li>
                <li><strong>기술적 조치:</strong> 비밀번호 암호화(BCrypt 등) 저장, SSL/TLS 통신 암호화, 접근통제 시스템 운영, 보안 취약점 정기 점검</li>
                <li><strong>물리적 조치:</strong> 클라우드 서버 접근 통제 및 방화벽 운영</li>
              </ol>
            </section>

            <section>
              <h3 className="font-bold text-base mb-2">제10조 (인증 토큰 및 세션 관리)</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>회사는 로그인 상태를 안전하게 유지하기 위해 인증 토큰을 발급하며, 위변조 방지 등 기술적 보안조치를 적용합니다.</li>
                <li>로그아웃하면 인증 토큰이 즉시 무효화되어 로그인 상태가 해제됩니다.</li>
                <li>세션은 기본 30분간 활동이 없으면 자동 만료되며, 만료된 세션 정보는 자동 삭제됩니다.</li>
              </ol>
            </section>

            <section>
              <h3 className="font-bold text-base mb-2">제11조 (개인정보 보호책임자)</h3>
              <div className="text-gray-700 leading-relaxed space-y-2">
                <p>회사는 개인정보 처리에 관한 업무를 총괄하고, 이용자의 불만처리 및 피해구제를 위해 아래와 같이 개인정보 보호책임자를 지정합니다.</p>
                <div className="ml-4 mt-2 bg-gray-50 p-3 rounded">
                  <p className="font-semibold">개인정보 보호책임자</p>
                  <p>• 직책: 정보보호담당</p>
                  <p>• 이메일: alt.generation@outlook.com</p>
                  <p>• 전화: 054-776-0881</p>
                </div>
                <p className="mt-2">개인정보 처리와 관련한 문의, 불만, 피해구제 등은 위 연락처로 문의하시면 지체 없이 처리하겠습니다.</p>
              </div>
            </section>

            <section>
              <h3 className="font-bold text-base mb-2">제12조 (개인정보 처리방침의 변경)</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>본 방침의 내용 추가삭제변경 시, 시행 7일 전(이용자 권리에 중요한 변경은 30일 전)까지 서비스 공지사항을 통해 공지합니다.</li>
                <li>변경 사항은 공지된 시행일부터 효력이 발생합니다.</li>
              </ol>
            </section>

            <section>
              <h3 className="font-bold text-base mb-2">제13조 (권익침해 구제방법)</h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                개인정보 침해로 인한 구제를 받기 위해 아래 기관에 분쟁해결 및 상담을 신청할 수 있습니다.
              </p>
              <div className="ml-4 space-y-1 text-gray-700">
                <p>• 개인정보분쟁조정위원회: 1833-6972 (www.kopico.go.kr)</p>
                <p>• 개인정보침해신고센터: 118 (privacy.kisa.or.kr)</p>
                <p>• 대검찰청 사이버수사과: 1301 (www.spo.go.kr)</p>
                <p>• 경찰청 사이버수사국: 182 (ecrm.cyber.go.kr)</p>
              </div>
            </section>

            <div className="pt-4 border-t mt-6 space-y-1">
              <p className="text-gray-600 text-xs">공고일자: 2026년 3월 27일</p>
              <p className="text-gray-600 text-xs">시행일자: 2026년 4월 1일</p>
              <p className="text-gray-600 text-xs">개정일자: 2026년 7월 24일 (개인정보 처리위탁 업체에 토스페이먼츠·바로빌 추가)</p>
            <p className="text-gray-600 text-xs">개정일자: 2026년 7월 26일 (국외 이전 조항을 위탁 조항에서 분리·신설, AWS 국내 서버 명시, 관리자 접속기록 보관기간을 2년으로 명시)</p>
              <p className="text-gray-600 text-xs">개정일자: 2026년 8월 10일 (AI 모델 학습 관련 문구 정리, 국외이전 대상별 실제 보유기간 및 번역 처리 목적 명시, 이용자 권리별 처리기간 세분화, 인증 토큰 관련 기술적 세부사항 간소화)</p>
              <p className="text-gray-600 text-xs">개정일자: 2026년 8월 13일 (웹사이트 이용 통계 분석을 위한 Google Analytics(GA4) 도입에 따라 자동수집항목·위탁·국외이전 조항에 관련 내용 및 수집 거부 방법 추가)</p>
              <p className="text-gray-600 text-xs">개정일자: 2026년 8월 14일 (웹사이트 이용 통계 분석 도구를 Google Analytics(GA4)에서 Umami로 변경함에 따라 자동수집항목·위탁·국외이전 조항 및 수집 거부 방법 갱신)</p>
              <p className="text-gray-600 text-xs">개정일자: 2026년 9월 2일 (운영주체 변경에 따라 회사명을 지호아이앤씨에서 유니버싱정보출판으로 변경)</p>
              <p className="text-xs pt-1">
                <Link to="/privacy/archive" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                  이전 버전 보기
                </Link>
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
